import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, Search, Ticket, X } from "lucide-react";
import { formatCash } from "../../utils/operatorConsoleHelpers";

const JoinedBetsChrome = ({
  ticketCount,
  totalStaked,
  totalPotential,
  refreshing,
  onRefresh,
  search,
  onSearchChange,
}) => {
  const inputRef = useRef(null);
  const searchWrapRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const hasQuery = Boolean(String(search || "").trim());
  const isExpanded = searchOpen || hasQuery;

  const openSearch = useCallback(() => setSearchOpen(true), []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    onSearchChange("");
  }, [onSearchChange]);

  useEffect(() => {
    if (searchOpen && inputRef.current) inputRef.current.focus();
  }, [searchOpen]);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const handlePointerDown = (event) => {
      if (searchWrapRef.current?.contains(event.target)) return;
      if (!hasQuery) setSearchOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (hasQuery) onSearchChange("");
      else setSearchOpen(false);
      inputRef.current?.blur();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isExpanded, hasQuery, onSearchChange]);

  const handleBlur = () => {
    window.setTimeout(() => {
      if (
        !String(search || "").trim() &&
        !searchWrapRef.current?.contains(document.activeElement)
      ) {
        setSearchOpen(false);
      }
    }, 120);
  };

  const titleLine = [
    `${ticketCount} slip${ticketCount === 1 ? "" : "s"}`,
    `${formatCash(totalStaked, { compact: true })} staked`,
    `${formatCash(totalPotential, { compact: true })} up`,
  ].join(" · ");

  return (
    <header className="jb-chrome">
      <div className="jb-chrome-row">
        <div className="jb-chrome-copy">
          <p className="jb-chrome-eyebrow">
            <Ticket size={9} aria-hidden />
            My tickets
          </p>
          <span className="jb-chrome-title-line">{titleLine}</span>
        </div>

        <div className="jb-chrome-actions-wrap">
          <div
            ref={searchWrapRef}
            className={`jb-chrome-search ${isExpanded ? "jb-chrome-search-open" : ""} ${
              hasQuery ? "jb-chrome-search-active" : ""
            }`}
          >
            {!isExpanded ? (
              <button
                type="button"
                className="jb-chrome-search-toggle"
                onClick={openSearch}
                aria-label="Search tickets"
                aria-expanded={false}
              >
                <Search size={13} strokeWidth={2.3} aria-hidden />
              </button>
            ) : (
              <>
                <Search size={13} aria-hidden className="jb-chrome-search-icon" />
                <input
                  ref={inputRef}
                  type="search"
                  className="jb-chrome-search-input"
                  placeholder="House or team…"
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onBlur={handleBlur}
                  aria-label="Search tickets"
                  aria-expanded
                />
                {hasQuery ? (
                  <button
                    type="button"
                    className="jb-chrome-search-clear"
                    onClick={() => onSearchChange("")}
                    aria-label="Clear search"
                  >
                    <X size={12} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="jb-chrome-search-clear"
                    onClick={closeSearch}
                    aria-label="Close search"
                  >
                    <X size={12} />
                  </button>
                )}
              </>
            )}
          </div>

          <button
            type="button"
            className="jb-chrome-btn"
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Refresh tickets"
            title="Refresh"
          >
            <RefreshCw
              size={13}
              strokeWidth={2.3}
              className={refreshing ? "animate-spin" : ""}
            />
          </button>
        </div>
      </div>
    </header>
  );
};

export default JoinedBetsChrome;
